import type { TitlePlate } from '@nine/shared';
import type { Pix } from '../icons/pix';
import { canvas, ctx2d } from '../render/pixel';
import { type PlateGeom, platePix, playPlate, spritePix, threadSprites } from './compose';
import { plateDef, type PlateDef, type PlateSprite } from './registry';

/**
 * Title plates (ฉายา, docs/art/titles-ref/): the decorated frame a title is shown in, over its
 * bearer's head, in the title list and in the character panel. UI art, drawn at screen resolution
 * on the UI plane (density option C): a plate is drawn at a whole number of pixels per unit, `k`,
 * picked from the screen's scale, so its pixels stay hard at any zoom. The title stays live text in
 * the game's font over it.
 *
 * Everything goes through `drawTitlePlateAt`, which takes a point on the screen: the 2D labels
 * pass, the panels (plates/dom.ts), the review page and the 3D overlay alike.
 */
import.meta.glob('./themes/*.ts', { eager: true });

export { plateDef, plateDefs } from './registry';
export type { PlateDef } from './registry';

/** The lettering's face and size at 1× (the game's UI face, as the name tags). */
export const PLATE_FONT_PX = 12;
const fontAt = (k: number) => `600 ${PLATE_FONT_PX * k}px Mitr, system-ui, sans-serif`;

/** Fonts that finish loading change every measure: start the caches again. */
let fontGen = 0;
if (typeof document !== 'undefined')
  document.fonts?.addEventListener('loadingdone', () => {
    fontGen++;
    plates.clear();
    letters.clear();
    widths.clear();
  });

const toCanvas = (pix: Pix): HTMLCanvasElement => {
  const c = canvas(Math.max(1, pix.w), Math.max(1, pix.h));
  const g = ctx2d(c);
  const img = g.createImageData(c.width, c.height);
  new Uint32Array(img.data.buffer).set(pix.d.subarray(0, c.width * c.height));
  g.putImageData(img, 0, 0);
  return c;
};

/** Keeps a map at most `n` entries long, dropping the oldest. */
function remember<V>(map: Map<string, V>, key: string, v: V, n: number): V {
  if (map.size >= n) map.delete(map.keys().next().value!);
  map.set(key, v);
  return v;
}

let probe: CanvasRenderingContext2D | null = null;
const measure = (text: string, k: number): TextMetrics => {
  probe ??= ctx2d(canvas(1, 1));
  probe.font = fontAt(k);
  return probe.measureText(text);
};

const widths = new Map<string, number>();
/**
 * The lettering's width in units at `k` pixels per unit (what the frame is stretched to): measured
 * once per title, size and font load, not every frame.
 */
function textUnits(text: string, k: number): number {
  const key = `${text}|${k}|${fontGen}`;
  const hit = widths.get(key);
  if (hit !== undefined) return hit;
  return remember(widths, key, Math.ceil(measure(text, k).width / k) + 1, 512);
}

const letters = new Map<string, { img: HTMLCanvasElement; cx: number; cy: number }>();

/** The title's lettering at `k`: live text in the plate's colours, outlined; (cx, cy) is its centre. */
function lettering(def: PlateDef, text: string, k: number) {
  const key = `${def.id}|${k}|${text}|${fontGen}`;
  const hit = letters.get(key);
  if (hit) return hit;
  const m = measure(text, k);
  const line = Math.max(2, Math.round(2.5 * k));
  const pad = line + 2;
  const left = Math.ceil(m.actualBoundingBoxLeft);
  const asc = Math.ceil(m.actualBoundingBoxAscent);
  const desc = Math.ceil(m.actualBoundingBoxDescent);
  const img = canvas(left + Math.ceil(m.actualBoundingBoxRight) + pad * 2, asc + desc + pad * 2);
  const g = ctx2d(img);
  g.font = fontAt(k);
  g.textAlign = 'left';
  g.textBaseline = 'alphabetic';
  g.lineJoin = 'round';
  const bx = pad + left;
  const by = pad + asc;
  g.lineWidth = line;
  g.strokeStyle = def.text.line;
  g.strokeText(text, bx, by);
  const gr = g.createLinearGradient(0, by - asc, 0, by + desc);
  gr.addColorStop(0, def.text.fill[0]);
  gr.addColorStop(0.45, def.text.fill[0]);
  gr.addColorStop(1, def.text.fill[1]);
  g.fillStyle = gr;
  g.fillText(text, bx, by);
  // Centred on its ink, so the marks above and below Thai letters balance in the frame.
  const made = {
    img,
    cx: bx - left + (left + m.actualBoundingBoxRight) / 2,
    cy: by - asc + (asc + desc) / 2,
  };
  return remember(letters, key, made, 512);
}

const plates = new Map<string, { img: HTMLCanvasElement; geom: PlateGeom }>();

/** A plate's still picture for a title at `k`, drawn once and kept. */
function still(def: PlateDef, text: string, k: number) {
  const tw = textUnits(text, k);
  const key = `${def.id}|${tw}|${k}|${fontGen}`;
  const hit = plates.get(key);
  if (hit) return hit;
  const { pix, geom } = platePix(def, tw, k);
  return remember(plates, key, { img: toCanvas(pix), geom }, 256);
}

const sprites = new Map<string, HTMLCanvasElement>();
const allSprites = new Map<TitlePlate, Record<string, PlateSprite>>();

function sprite(def: PlateDef, name: string, k: number): HTMLCanvasElement | null {
  const key = `${def.id}|${name}|${k}`;
  const hit = sprites.get(key);
  if (hit) return hit;
  let all = allSprites.get(def.id);
  if (!all) {
    all = { ...threadSprites(def), ...(def.motion?.sprites ?? {}) };
    allSprites.set(def.id, all);
  }
  const spr = all[name];
  if (!spr) return null;
  return remember(sprites, key, toCanvas(spritePix(def, spr, k)), 1024);
}

/** A title plate on the screen: where its frame landed, in the caller's coordinates. */
export interface PlateRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface PlateOpts {
  readonly plate: TitlePlate;
  readonly text: string;
  /** Seconds, for the plate's motion: pass `plateClock(t)` (0 holds it still). */
  readonly t?: number;
  /** Units per screen pixel of the current transform (1: one unit per CSS pixel at zoom 1). */
  readonly scale?: number;
}

/**
 * Draws a title plate with the lettering's centre at (x, y) horizontally and the frame's bottom
 * edge at y, in `ctx`'s current coordinates (a screen point: the 2D name tags, the 3D overlay's
 * projected head, a panel's canvas). The plate's pixels are snapped to the device grid at a whole
 * number of pixels per unit. Returns the frame's rect in the same coordinates.
 */
export function drawTitlePlateAt(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  o: PlateOpts,
): PlateRect | null {
  const def = plateDef(o.plate);
  if (!def || !o.text) return null;
  const m = ctx.getTransform();
  const sc = Math.hypot(m.a, m.b) || 1;
  const k = Math.max(1, Math.round(sc * (o.scale ?? 1)));
  const { img, geom } = still(def, o.text, k);
  const dx = m.a * x + m.c * y + m.e;
  const dy = m.b * x + m.d * y + m.f;
  const ox = Math.round(dx - geom.tx * k);
  const oy = Math.round(dy - geom.frame.b * k);
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(img, ox, oy);
  const txt = lettering(def, o.text, k);
  ctx.drawImage(txt.img, Math.round(ox + geom.tx * k - txt.cx), Math.round(oy + geom.ty * k - txt.cy));
  const base = ctx.globalAlpha;
  playPlate(def, geom, o.t ?? 0, {
    spr(name, sx, sy, alpha = 1) {
      const c = sprite(def, name, k);
      if (!c || alpha <= 0) return;
      ctx.globalAlpha = base * Math.min(1, alpha);
      // Sprites carry a unit of room for their outline.
      ctx.drawImage(c, ox + Math.round((sx - 1) * k), oy + Math.round((sy - 1) * k));
    },
  });
  ctx.restore();
  const f = geom.frame;
  return {
    x: x + ((f.x - geom.tx) * k) / sc,
    y: y - (f.h * k) / sc,
    w: (f.w * k) / sc,
    h: (f.h * k) / sc,
  };
}

/** A plate's whole canvas at `k` for a title, in units, and where its anchor is (for laying it out). */
export function plateLayout(plate: TitlePlate, text: string, k: number): PlateGeom | null {
  const def = plateDef(plate);
  return def ? still(def, text, k).geom : null;
}
